import React, { useEffect, useMemo } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { IoIosClose } from 'react-icons/io'

import { FileThumbnail, Flex, IconButton } from '@hivewatch/dark'

import { ImageThumbnail } from './ImageThumbnail'
import { PdfThumbnail } from './PdfThumbnail'
import { VideoThumbnail } from './VideoThumbnail'

interface MediaThumbnailIProps {
  file: File
  removeFile: (file: File) => void
}

export const MediaThumbnail = ({ file, removeFile }: MediaThumbnailIProps) => {
  const colorScheme = useLightMode()
  // One object URL per file, revoked on change/unmount — AddNote re-renders
  // this tree on every keystroke, so an inline createObjectURL would mint a
  // new unreleased blob URL per render.
  const imageUrl = useMemo(
    () => (file.type.startsWith('image/') ? URL.createObjectURL(file) : null),
    [file]
  )
  useEffect(
    () => () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl)
    },
    [imageUrl]
  )

  const handleRemoveFile = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation()
    removeFile(file)
  }

  return (
    // No Tailwind classes on dark (v3) components: nativewind's babel
    // transform wraps @hivewatch/dark imports and passes the resolved
    // Tailwind styles as a style ARRAY, which real browsers reject with
    // "Failed to set an indexed property on CSSStyleDeclaration" — crashing
    // the drawer when a file is attached (ENG-32099 bug bash follow-up).
    // Hover-reveal uses a css child selector + data attribute instead.
    <Flex
      alignItems='center'
      css={{
        '&:hover [data-media-thumbnail-remove]': { visibility: 'visible' },
      }}
      justifyContent='center'
      position='relative'
    >
      <IconButton
        aria-label='Remove'
        borderRadius='full'
        colorScheme={colorScheme}
        data-media-thumbnail-remove=''
        dimension={20}
        icon={<IoIosClose size={18} />}
        onClick={handleRemoveFile}
        position='absolute'
        right='-5px'
        top='-8px'
        visibility='hidden'
        zIndex={10}
      />
      {file.type.startsWith('image/') && imageUrl && (
        <ImageThumbnail imgUrl={imageUrl} />
      )}
      {file.type === 'application/pdf' && <PdfThumbnail file={file} />}
      {file.type.startsWith('video/') && <VideoThumbnail videoFile={file} />}
      {/* Formats with no inline preview still need to be visible before upload */}
      {!file.type.startsWith('image/') &&
        file.type !== 'application/pdf' &&
        !file.type.startsWith('video/') && (
          <FileThumbnail
            colorScheme={colorScheme}
            name={file.name}
            width='137px'
          />
        )}
    </Flex>
  )
}
